export default {
  menu: {
    deleteActivity: {
      notes:
        'Menu item in the screen activity details header overflow menu that opens the delete confirmation dialog',
      message: 'Delete activity',
    },
  },
  deleteDialog: {
    title: {
      notes: 'Title of the dialog confirming deletion of a screen activity',
      message: 'Delete activity',
    },
    message: {
      notes: 'Body text of the dialog confirming deletion of a screen activity',
      message:
        'This will permanently delete this maintenance activity and all recorded data. This cannot be undone.',
    },
    deleted: {
      notes: 'Toast shown after a screen activity has been deleted',
      message: 'Screen activity deleted',
    },
  },
  unplannedActivity: {
    button: {
      notes:
        'Outlined button in the screen activity details header, shown while nothing is planned, that lets the user skip planning and go straight to recording maintenance',
      message: 'Unplanned activity',
    },
    dialog: {
      title: {
        notes:
          'Title of the dialog confirming that planning is skipped for a screen activity',
        message: 'Unplanned activity?',
      },
      message: {
        notes:
          'Body text of the dialog confirming that planning is skipped for a screen activity',
        message:
          'There is nothing planned for this activity. This will mark all decks as planned with empty plans, allowing the activity to proceed without a plan. This cannot be undone.',
      },
      confirm: {
        notes:
          'Confirm button in the dialog that skips planning for a screen activity',
        message: 'Proceed',
      },
    },
  },
  openConfig: {
    button: {
      notes:
        'Label of the mini button in the planned layout header on the screen activity plan tab that opens the select config dialog',
      message: 'Open',
    },
    tooltip: {
      notes:
        'Tooltip of the mini button in the planned layout header on the screen activity plan tab that opens the select config dialog',
      message: 'Open config',
    },
    blockers: {
      otherDeck: {
        notes:
          'Info text in the select config dialog when the selected configuration belongs to a deck other than the one being planned',
        message:
          'This configuration belongs to another deck and cannot be used for the deck being planned.',
      },
      sameConfig: {
        notes:
          'Info text in the select config dialog when the selected configuration is the one the activity already plans to install on the deck',
        message:
          'This configuration is already planned for installation on this deck.',
      },
      alreadyInstalled: {
        notes:
          'Info text in the select config dialog when the selected configuration is the one currently installed on the deck, and the deck is already planned as a maintenance of that configuration',
        message:
          'This configuration is already installed on the deck. Its maintenance is what is being planned.',
      },
      incomplete: {
        notes:
          'Info text in the select config dialog when the selected configuration leaves deck positions without screening media. {{count}} is the number of positions',
        message: {
          one: 'This configuration cannot be installed: 1 position has no screening media. Complete the configuration first.',
          other:
            'This configuration cannot be installed: {{count}} positions have no screening media. Complete the configuration first.',
        },
      },
    },
  },
  lifecycle: {
    header: {
      notes: 'Header at the top of the screen activity lifecycle popper',
      message: 'Screen activity',
    },
    status: {
      planning: {
        notes:
          'Lifecycle status shown when planning is still in progress, on the trigger button and deck row chips',
        message: 'Planning',
      },
      planned: {
        notes:
          'Lifecycle status shown when planning is complete but execution has not finished, on the trigger button and deck row chips',
        message: 'Planned',
      },
      performed: {
        notes:
          'Lifecycle status shown when execution is complete, on the trigger button and deck row chips',
        message: 'Performed',
      },
      unplanned: {
        notes:
          'Lifecycle status shown when no plan exists, on the trigger button and deck row chips',
        message: 'Unplanned',
      },
    },
    planSummary: {
      notes:
        'Plan summary line on a deck row in the lifecycle popper. {{count}} is the number of planned actions',
      message: {
        zero: 'Plan: No actions planned',
        one: 'Plan: 1 action planned',
        other: 'Plan: {{count}} actions planned',
      },
    },
    executionLocked: {
      notes:
        'Execution summary line on a deck row in the lifecycle popper while planning is not yet completed',
      message: 'Execution locked',
    },
    executionSummary: {
      notes:
        'Execution progress line on a deck row in the lifecycle popper. {{done}} is the number of performed actions, {{total}} the number of planned actions',
      message: 'Execution: {{done}} / {{total}} done',
    },
    back: {
      notes:
        'Accessible label for the back button in the lifecycle popper confirm steps',
      message: 'Back',
    },
    actions: {
      markAsPlanned: {
        notes:
          'Deck row action and confirm button that locks the plan for a deck with planned actions',
        message: 'Mark as planned',
      },
      skip: {
        notes:
          'Deck row action that locks the plan for a deck without any planned actions',
        message: 'Skip',
      },
      markAsPerformed: {
        notes:
          'Deck row action and confirm button that marks a planned deck as performed',
        message: 'Mark as performed',
      },
    },
    confirmPlanned: {
      title: {
        notes:
          'Title of the mark as planned confirm step. {{deckName}} is the name of the deck',
        message: 'Mark as planned — {{deckName}}',
      },
      body: {
        notes:
          'Body of the mark as planned confirm step. {{count}} is the number of planned actions',
        message: {
          one: '1 action planned. Once marked as planned, the plan cannot be edited.',
          other:
            '{{count}} actions planned. Once marked as planned, the plan cannot be edited.',
        },
      },
    },
    confirmSkip: {
      title: {
        notes:
          'Title of the skip deck confirm step. {{deckName}} is the name of the deck',
        message: 'Skip deck — {{deckName}}',
      },
      info: {
        notes: 'Info message in the skip deck confirm step',
        message:
          'No actions are planned for this deck. Skipping will mark it as planned with an empty plan, allowing the activity to proceed.',
      },
      body: {
        notes: 'Body of the skip deck confirm step',
        message:
          'Planning will be locked for this deck. This cannot be reversed.',
      },
      confirm: {
        notes: 'Confirm button in the skip deck confirm step',
        message: 'Skip deck',
      },
    },
    missingPanels: {
      title: {
        notes:
          'Title of the info step shown when a deck cannot be marked as planned because positions are missing screening media. {{deckName}} is the name of the deck',
        message: 'Cannot mark as planned — {{deckName}}',
      },
      warning: {
        notes:
          'Warning in the missing panels info step. {{count}} is the number of positions without screening media in the planned layout',
        message: {
          one: '1 position in the planned layout is missing screening media.',
          other:
            '{{count}} positions in the planned layout are missing screening media.',
        },
      },
      body: {
        notes: 'Body of the missing panels info step',
        message:
          'A deck cannot be marked as planned unless the resulting layout is completely filled. Plan replacements for the empty positions, then try again.',
      },
    },
    confirmPerformed: {
      title: {
        notes:
          'Title of the mark as performed confirm step. {{deckName}} is the name of the deck',
        message: 'Mark as performed — {{deckName}}',
      },
      pendingWarning: {
        notes:
          'Warning in the mark as performed confirm step when actions are still pending. {{count}} is the number of pending actions',
        message: {
          one: '1 action still pending. Resolve before completing.',
          other: '{{count}} actions still pending. Resolve before completing.',
        },
      },
      bulkSkip: {
        notes:
          'Button in the mark as performed confirm step that skips all pending actions. {{count}} is the number of pending actions',
        message: {
          one: 'Bulk-skip 1 remaining',
          other: 'Bulk-skip {{count}} remaining',
        },
      },
      allResolved: {
        notes:
          'Message in the mark as performed confirm step when no actions are pending',
        message: 'All actions resolved.',
      },
      body: {
        notes: 'Body of the mark as performed confirm step',
        message:
          'Once completed, execution cannot be edited. This action is irreversible.',
      },
      pendingTooltip: {
        notes:
          'Tooltip on the disabled mark as performed button while actions are still pending',
        message: 'Resolve all pending actions first',
      },
    },
  },
  plannedActions: {
    header: {
      notes:
        'Header for the planned actions accordion on the activity selection details pane',
      message: 'Planned actions',
    },
    actionType: {
      measure: {
        notes:
          'Row label for actions of type measure in the planned actions table',
        message: 'Measurement',
      },
      replace: {
        notes:
          'Row label for actions of type replace in the planned actions table',
        message: 'Replace',
      },
      rotate: {
        notes:
          'Row label for actions of type rotate in the planned actions table',
        message: 'Rotate',
      },
      relocate: {
        notes:
          'Row label for actions of type relocate in the planned actions table',
        message: 'Relocate',
      },
      swap: {
        notes:
          'Row label for actions of type swap in the planned actions table',
        message: 'Swap',
      },
      patch: {
        notes:
          'Row label for actions of type patch in the planned actions table',
        message: 'Patch',
      },
    },
    overview: {
      actionColumn: {
        notes:
          'Column header for the action type in the planned actions overview shown when no panels are selected',
        message: 'Action',
      },
      plannedColumn: {
        notes:
          'Column header for the number of positions with a planned action in the planned actions overview',
        message: 'Planned',
      },
      empty: {
        notes:
          'Shown instead of the planned actions overview when the deck has no planned actions',
        message: 'No planned actions yet. Select panels to begin planning.',
      },
    },
    plannedValue: {
      measure: {
        notes:
          'Value shown in the planned column when a measure action is planned',
        message: 'Measure',
      },
      patch: {
        notes:
          'Value shown in the planned column and offered in the patch row edit dropdown when a patch action is planned',
        message: 'Yes',
      },
    },
    editActions: {
      replaceWithSame: {
        notes:
          'Option in the replace row edit dropdown that plans replacements using the same screening media as initially installed. Also offered on a pending unplanned replace in the maintenance actions table, where it records the replace as performed with the initial media',
        message: 'Replace with same',
      },
      replaceWithDifferent: {
        notes:
          'Option in the replace row edit dropdown that opens the screening media selection dialog. Also offered on a pending unplanned replace in the maintenance actions table',
        message: 'Replace with different...',
      },
      relocateTo: {
        notes:
          'Option in the relocate row edit dropdown that opens the dialog for choosing where to move the selected panel',
        message: 'Relocate to...',
      },
      swapWith: {
        notes:
          'Option in the relocate row edit dropdown that opens the dialog for choosing which panel to swap the selected panel with',
        message: 'Swap with...',
      },
      changeTarget: {
        notes:
          'Option in the relocate row edit dropdown that reopens the dialog for a planned relocate or swap',
        message: 'Change target...',
      },
      remove: {
        notes:
          'Option in the relocate row edit dropdown that removes the planned relocate or swap',
        message: 'Remove',
      },
    },
    transferDialog: {
      title: {
        relocate: {
          notes:
            'Title of the dialog for planning a relocate. {{position}} is the position of the panel being moved',
          message: 'Relocate panel {{position}}',
        },
        swap: {
          notes:
            'Title of the dialog for planning a swap. {{position}} is the position of the panel being swapped',
          message: 'Swap panel {{position}}',
        },
      },
      mode: {
        relocate: {
          notes:
            'Toggle in the relocate dialog that switches to moving the panel to an empty or occupied position',
          message: 'Relocate',
        },
        swap: {
          notes:
            'Toggle in the relocate dialog that switches to exchanging the panel with another one',
          message: 'Swap',
        },
      },
      selectTarget: {
        notes:
          'Hint in the relocate dialog before a target position has been clicked in the preview',
        message: 'Click the position to move the panel to.',
      },
      legend: {
        source: {
          notes:
            'Legend entry in the relocate dialog preview for the icon marking the position the panel leaves',
          message: 'Source',
        },
        target: {
          notes:
            'Legend entry in the relocate dialog preview for the icon marking the position the panel moves to',
          message: 'Target',
        },
        swap: {
          notes:
            'Legend entry in the relocate dialog preview for the icon marking the two panels being swapped',
          message: 'Swapped panels',
        },
      },
      issues: {
        sourceEmpty: {
          notes:
            'Error in the relocate dialog when the selected position holds no panel. {{positions}} is the position',
          message: 'There is no panel at {{positions}} to move.',
        },
        sameAsSource: {
          notes:
            'Error in the relocate dialog when the clicked target is the position the panel already has',
          message: 'The target is the same as the source.',
        },
        outsideGrid: {
          notes:
            'Error in the relocate dialog when the moved panel would extend beyond the deck. {{positions}} is the clicked target',
          message: 'The panel does not fit on the deck at {{positions}}.',
        },
        overlapsSource: {
          notes:
            'Error in the relocate dialog when the target footprint overlaps the footprint of the panel being moved',
          message: 'The target overlaps the position the panel is moved from.',
        },
        swapWithEmpty: {
          notes:
            'Error in the relocate dialog when swapping with a position without a panel. {{positions}} is the clicked target',
          message: 'There is no panel at {{positions}} to swap with.',
        },
        differentSizes: {
          notes:
            'Error in the relocate dialog when the two panels to swap have different sizes. {{positions}} are the two positions',
          message: 'The panels at {{positions}} have different sizes.',
        },
        actionsRemovedAtSource: {
          notes:
            'Warning in the relocate dialog that planned actions on the moved panel will be removed. {{positions}} are the positions',
          message:
            'Planned actions on the moved panel at {{positions}} will be removed.',
        },
        actionsRemovedAtTarget: {
          notes:
            'Warning in the relocate dialog that planned actions at the target will be removed. {{positions}} are the positions',
          message: 'Planned actions at {{positions}} will be removed.',
        },
        existingTransferRemoved: {
          notes:
            'Warning in the relocate dialog that other planned relocates or swaps touching the source or target will be removed. {{positions}} are the positions those panels move from',
          message:
            'Planned relocates or swaps of the panels at {{positions}} will be removed.',
        },
        panelDisplaced: {
          notes:
            'Warning in the relocate dialog that the panels at the target are taken out. {{positions}} are their positions',
          message:
            'The panel at {{positions}} is taken out and disappears from the plan.',
        },
        blockedBySourceActions: {
          notes:
            'Error in the relocate dialog when recording an unplanned move of a panel that has planned actions. {{positions}} are the positions',
          message:
            'The panel at {{positions}} has planned actions, so the move cannot be recorded.',
        },
        blockedByTargetActions: {
          notes:
            'Error in the relocate dialog when recording an unplanned move onto positions with planned actions. {{positions}} are the positions',
          message:
            'There are planned actions at {{positions}}, so the move cannot be recorded.',
        },
        blockedByTransfer: {
          notes:
            'Error in the relocate dialog when recording an unplanned move that touches a planned relocate or swap. {{positions}} are the positions those panels move from',
          message:
            'The panels at {{positions}} have a planned relocate or swap, so the move cannot be recorded.',
        },
        sourceLeftEmpty: {
          notes:
            'Info in the relocate dialog that the source position needs a replacement to be planned. {{positions}} is the source position',
          message:
            '{{positions}} is left empty. Plan a replacement there before marking the deck as planned.',
        },
        wearReset: {
          notes:
            'Info in the relocate dialog that moving panels resets the wear history of the affected positions',
          message:
            'Wear history at the source and target positions resets after this maintenance.',
        },
      },
      confirm: {
        relocate: {
          notes: 'Confirm button in the relocate dialog in relocate mode',
          message: 'Relocate',
        },
        swap: {
          notes: 'Confirm button in the relocate dialog in swap mode',
          message: 'Swap',
        },
      },
    },
  },
  maintenanceActions: {
    emptySelection: {
      notes:
        'Shown above the maintenance actions table in the selection details pane when no panels are selected',
      message: 'All actions',
    },
    empty: {
      notes:
        'Shown instead of the maintenance actions table when the selected panels have no planned or unplanned actions',
      message:
        'No maintenance actions recorded for this selection. Use Add unplanned action to log extra work.',
    },
    unplannedRow: {
      notes:
        'Row label in the maintenance actions table for actions recorded as unplanned. {{action}} is the action type label',
      message: '{{action}} (unplanned)',
    },
    addUnplanned: {
      button: {
        notes:
          'Button below the maintenance actions table that opens the menu for recording an action that was not planned',
        message: 'Add unplanned action',
      },
    },
    columns: {
      action: {
        notes:
          'Column header for the action type in the maintenance actions table',
        message: 'Action',
      },
      position: {
        notes:
          'Column header for the panel positions in the maintenance actions table',
        message: 'Position',
      },
      initial: {
        notes:
          'Column header for the initially installed screening media in the maintenance actions table',
        message: 'Initial',
      },
      planned: {
        notes:
          'Column header for the planned screening media in the maintenance actions table',
        message: 'Planned',
      },
      performed: {
        notes:
          'Column header for the execution outcome in the maintenance actions table',
        message: 'Performed',
      },
    },
    overview: {
      plannedColumn: {
        notes:
          'Column header for the number of planned actions in the maintenance overview shown when no panels are selected',
        message: 'Planned',
      },
      performedColumn: {
        notes:
          'Column header for the number of actions no longer pending (performed or skipped) in the maintenance overview',
        message: 'Performed',
      },
      unplannedColumn: {
        notes:
          'Column header for the number of unplanned actions in the maintenance overview',
        message: 'Unplanned',
      },
      empty: {
        notes:
          'Shown instead of the maintenance overview when the deck has no maintenance actions',
        message: 'No maintenance actions.',
      },
    },
    position: {
      relocate: {
        notes:
          'Position of a relocate action in the maintenance actions table. {{source}} is the position the panel is moved from, {{target}} the position it is moved to',
        message: '{{source}} → {{target}}',
      },
      swap: {
        notes:
          'Position of a swap action in the maintenance actions table. {{source}} and {{target}} are the two positions exchanging panels',
        message: '{{source}} ⇄ {{target}}',
      },
    },
    plannedValue: {
      patch: {
        notes:
          'Value shown in the planned column of the maintenance actions table for a patch action',
        message: 'Yes',
      },
      relocateTo: {
        notes:
          'Value shown in the planned column, or in the performed column for an unplanned action, for a relocate when the selection contains the position the panel is moved from. {{position}} is the position it is moved to',
        message: '→ {{position}}',
      },
      swapWith: {
        notes:
          'Value shown in the planned column, or in the performed column for an unplanned action, for a swap when the selection contains the first of the two positions. {{position}} is the other position',
        message: '⇄ {{position}}',
      },
      from: {
        notes:
          'Value shown in the planned column, or in the performed column for an unplanned action, for a relocate or swap when the selection only contains the position a panel is moved to. {{position}} is the position it comes from',
        message: '← {{position}}',
      },
    },
    performedValue: {
      patch: {
        notes:
          'Value shown in the performed column of the maintenance actions table for an unplanned patch action',
        message: 'Patched',
      },
    },
    editActions: {
      performAsPlanned: {
        notes:
          'Option in the performed dropdown that marks the actions as performed as planned',
        message: 'Perform as planned',
      },
      skipped: {
        notes:
          'Option in the performed dropdown that marks the actions as skipped',
        message: 'Skipped',
      },
      otherMaterial: {
        notes:
          'Option in the performed dropdown of a planned replace row that opens the screening media dialog; the planned replace is skipped and an unplanned replace with the picked media is recorded',
        message: 'Other material...',
      },
      revertToPending: {
        notes:
          'Option in the performed dropdown that reverts the actions to pending',
        message: 'Revert to pending',
      },
      removeUnplanned: {
        notes:
          'Option in the performed dropdown of an unplanned row that deletes the recorded unplanned actions',
        message: 'Remove unplanned action',
      },
    },
  },
  notes: {
    addNote: {
      button: {
        notes:
          'Button in the selection details header on the notes tab that opens the menu for adding a note',
        message: 'Add note',
      },
      note: {
        notes:
          'Menu item under the add note button that opens the dialog for a plain note',
        message: 'Note',
      },
      recommendation: {
        notes:
          'Menu item under the add note button that opens the dialog for a note flagged as a recommendation',
        message: 'Recommendation',
      },
      noPlan: {
        notes:
          'Tooltip on the disabled add note button when the selected deck has no config installation or deck maintenance in the activity to attach notes to',
        message: 'The deck has no plan in this activity.',
      },
    },
    dialog: {
      addTitle: {
        notes: 'Title of the note dialog when adding a plain note',
        message: 'Add note',
      },
      addRecommendationTitle: {
        notes:
          'Title of the note dialog when adding a note flagged as a recommendation',
        message: 'Add recommendation',
      },
      editTitle: {
        notes: 'Title of the note dialog when editing an existing note',
        message: 'Edit note',
      },
      deck: {
        notes: 'Label of the deck select in the note dialog',
        message: 'Deck',
      },
      recommendation: {
        notes: 'Label of the recommendation checkbox in the note dialog',
        message: 'Recommendation',
      },
      note: {
        notes: 'Label of the note text field in the note dialog',
        message: 'Note',
      },
      notePlaceholder: {
        notes: 'Placeholder of the empty note text field in the note dialog',
        message: 'Write a note…',
      },
      phase: {
        notes:
          'Label of the maintenance phase select in the note dialog, saying which phase of the maintenance the note is about',
        message: 'Phase',
      },
      phaseOptions: {
        none: {
          notes:
            'Option in the phase select of the note dialog for a note not tied to a maintenance phase',
          message: '-',
        },
        pre: {
          notes:
            'Option in the phase select of the note dialog for a note about the deck before maintenance',
          message: 'Pre-maintenance',
        },
        mid: {
          notes:
            'Option in the phase select of the note dialog for a note made during maintenance',
          message: 'Mid-maintenance',
        },
        post: {
          notes:
            'Option in the phase select of the note dialog for a note about the deck after maintenance',
          message: 'Post-maintenance',
        },
      },
      positions: {
        notes:
          'Label of the positions field in the note dialog, listing the panel positions the note concerns',
        message: 'Positions',
      },
      selectPositions: {
        notes:
          'Tooltip on the button in the positions field of the note dialog that opens the deck grid for picking positions',
        message: 'Select positions',
      },
      save: {
        notes: 'Confirm button in the note dialog',
        message: 'Save',
      },
      addImageTitle: {
        notes:
          'Title of the note dialog when it was opened from one of the image menu items, with files already chosen',
        message: 'Add image',
      },
      attachments: {
        notes: 'Label above the image tiles in the note dialog',
        message: 'Images',
      },
      addImage: {
        notes:
          'Tooltip on the plus tile in the note dialog that opens the file picker for more images',
        message: 'Add image',
      },
      removeImage: {
        notes:
          'Tooltip on the remove button of an image tile in the note dialog',
        message: 'Remove image',
      },
      imagesFull: {
        notes:
          'Tooltip on the disabled plus tile in the note dialog when the note already has the maximum number of images. {{max}} is that maximum',
        message: 'A note can have at most {{max}} images',
      },
    },
    rowActions: {
      edit: {
        notes: 'Tooltip on the edit icon button of a row in the notes table',
        message: 'Edit note',
      },
      delete: {
        notes: 'Tooltip on the delete icon button of a row in the notes table',
        message: 'Delete note',
      },
      view: {
        notes:
          'Tooltip on the view icon button of a row in the previous activity notes table, opening the note read-only',
        message: 'View note',
      },
    },
    sections: {
      thisActivity: {
        notes:
          'Header of the accordion in the notes selection details pane holding the notes made on the deck in this activity',
        message: 'This activity',
      },
      previousActivity: {
        notes:
          'Header of the accordion in the notes selection details pane holding the notes made on the deck in its previous maintenance activity',
        message: 'Previous activity',
      },
    },
    deleteDialog: {
      title: {
        notes: 'Title of the dialog confirming deletion of a single note',
        message: 'Delete note?',
      },
      message: {
        notes: 'Body text of the dialog confirming deletion of a single note',
        message:
          'This will permanently delete the note. This cannot be undone.',
      },
    },
    clearAll: {
      button: {
        notes:
          'Button below the notes table that opens the dialog confirming deletion of all notes made on the deck in this activity',
        message: 'Clear all notes',
      },
      title: {
        notes:
          'Title of the dialog confirming deletion of all notes made on the deck in this activity',
        message: 'Clear all notes?',
      },
      message: {
        notes:
          'Body text of the dialog confirming deletion of all notes made on the deck in this activity',
        message:
          'This will permanently delete all notes added to this deck in this maintenance activity. Notes from previous maintenance activities are kept.',
      },
    },
    emptySelection: {
      notes:
        'Shown above the notes table in the selection details pane when no panels are selected',
      message: 'All notes',
    },
    empty: {
      notes:
        'Shown instead of the notes table when the deck has no notes in this activity',
      message: 'No notes yet.',
    },
    emptyForSelection: {
      notes:
        'Shown instead of the notes table when none of the notes concern the selected panels',
      message: 'No notes for the selected positions.',
    },
    emptyPrevious: {
      notes:
        'Shown instead of the previous activity notes table when the deck had no notes in its previous maintenance activity',
      message: 'No notes from the previous maintenance.',
    },
    entireDeck: {
      notes:
        'Position shown in the notes table for a note that concerns the whole deck rather than specific positions',
      message: 'Entire deck',
    },
    columns: {
      type: {
        notes: 'Column header for the note type in the notes table',
        message: 'Type',
      },
      note: {
        notes: 'Column header for the note text in the notes table',
        message: 'Note',
      },
      position: {
        notes: 'Column header for the panel positions in the notes table',
        message: 'Position',
      },
      date: {
        notes: 'Column header for the creation date in the notes table',
        message: 'Date',
      },
      phase: {
        notes:
          'Column header for the maintenance phase (pre, mid, post) in the notes table',
        message: 'Phase',
      },
      recommendation: {
        notes:
          'Column header for whether the note is a recommendation in the notes table, abbreviated',
        message: 'Rec.',
      },
      attachments: {
        notes: 'Column header for the image thumbnails in the notes table',
        message: 'Images',
      },
    },
    attachments: {
      unsupportedType: {
        notes:
          'Error toast when a chosen file is not an accepted image type. {{fileName}} is the name of the file',
        message:
          '{{fileName}} was skipped: only JPEG, PNG and GIF images can be attached.',
      },
      tooLarge: {
        notes:
          'Error toast when a chosen file exceeds the size limit checked before upload. {{fileName}} is the name of the file, {{maxSize}} the limit in megabytes',
        message:
          '{{fileName}} was skipped: images must be at most {{maxSize}} MB.',
      },
      tooMany: {
        notes:
          'Error toast when more images were chosen than the note has room for. {{count}} is the number of files skipped, {{max}} the maximum number of images per note',
        message: {
          one: '1 file was skipped: a note can have at most {{max}} images.',
          other:
            '{{count}} files were skipped: a note can have at most {{max}} images.',
        },
      },
      rejectedAsTooLarge: {
        notes:
          'Error toast when the server rejected an image as too large after the note was saved. {{fileName}} is the name of the file',
        message: 'The server rejected {{fileName}} as too large.',
      },
      uploadFailed: {
        notes:
          'Error toast when uploading an image to a saved note failed. {{fileName}} is the name of the file',
        message: 'Failed to upload {{fileName}}.',
      },
      imageAlt: {
        notes:
          'Alt text of a note image thumbnail. {{index}} is the 1-based number of the image within the note',
        message: 'Image {{index}}',
      },
      more: {
        notes:
          'Shown after the first thumbnails in the notes table when a note has more images. {{count}} is the number of images not shown',
        message: '+{{count}}',
      },
    },
    types: {
      note: {
        notes:
          'Type label for a plain note in the notes table and panel tooltip',
        message: 'Note',
      },
      recommendation: {
        notes:
          'Type label for a note flagged as a recommendation in the notes table and panel tooltip',
        message: 'Recommendation',
      },
      preImage: {
        notes:
          'Type label for a note with images taken before maintenance in the notes table and panel tooltip',
        message: 'Pre-maintenance image',
      },
      midImage: {
        notes:
          'Type label for a note with images taken during maintenance in the notes table and panel tooltip',
        message: 'Mid-maintenance image',
      },
      postImage: {
        notes:
          'Type label for a note with images taken after maintenance in the notes table and panel tooltip',
        message: 'Post-maintenance image',
      },
    },
    phase: {
      none: {
        notes:
          'Value in the phase column of the notes table for a note not tied to a maintenance phase',
        message: '-',
      },
      pre: {
        notes:
          'Value in the phase column of the notes table for a note made before maintenance',
        message: 'Pre',
      },
      mid: {
        notes:
          'Value in the phase column of the notes table for a note made during maintenance',
        message: 'Mid',
      },
      post: {
        notes:
          'Value in the phase column of the notes table for a note made after maintenance',
        message: 'Post',
      },
    },
  },
  measureActions: {
    emptySelection: {
      notes:
        'Shown above the measurements table in the selection details pane when no panels are selected',
      message: 'All positions',
    },
    columns: {
      action: {
        notes:
          'Column header for the measurement type in the measurements table',
        message: 'Action',
      },
      position: {
        notes:
          'Column header for the panel positions in the measurements table',
        message: 'Position',
      },
      thisMaintenance: {
        notes:
          'Column header for the values measured during this maintenance in the measurements table',
        message: 'This maintenance',
      },
    },
    overview: {
      measurementColumn: {
        notes:
          'Column header for the measurement type in the measurements overview shown when no panels are selected',
        message: 'Measurement',
      },
      plannedColumn: {
        notes:
          'Column header for the number of positions with a planned measurement in the measurements overview',
        message: 'Planned',
      },
      measuredColumn: {
        notes:
          'Column header for the number of planned positions with a recorded value in the measurements overview',
        message: 'Measured',
      },
      unplannedColumn: {
        notes:
          'Column header for the number of unplanned positions with a recorded value in the measurements overview',
        message: 'Unplanned',
      },
    },
    rows: {
      workingDepth: {
        notes:
          'Row label for the remaining working depth in the measurements table',
        message: 'Working depth',
      },
      apertureWidth: {
        notes:
          'Row label for the current aperture width in the measurements table',
        message: 'Aperture width',
      },
    },
    validation: {
      negativeDepth: {
        notes:
          'Validation error below the working depth input in the measurements table when a negative value is entered',
        message: 'Working depth cannot be negative',
      },
      aboveNominalDepth: {
        notes:
          'Validation error below the working depth input in the measurements table when the value exceeds the nominal working depth of the installed screening media. {{limit}} is the nominal working depth in mm',
        message: 'Working depth cannot exceed the nominal {{limit}} mm',
      },
      belowNominalAperture: {
        notes:
          'Validation error below the aperture width input in the measurements table when the value is smaller than the nominal aperture width of the installed screening media. {{limit}} is the nominal aperture width in mm',
        message:
          'Aperture width cannot be smaller than the nominal {{limit}} mm',
      },
      abovePanelWidth: {
        notes:
          'Validation error below the aperture width input in the measurements table when the value exceeds the width of the panel. {{limit}} is the panel width in mm',
        message: 'Aperture width cannot exceed the panel width of {{limit}} mm',
      },
    },
    unplanned: {
      single: {
        notes:
          'Info message above the measurements table when the single selected position has no planned measurement',
        message: 'Unplanned measurement.',
      },
      multiple: {
        notes:
          'Info message above the measurements table when the selection contains positions without a planned measurement. {{count}} is the number of such positions',
        message: {
          one: '{{count}} position outside the planned scope will be recorded as unplanned.',
          other:
            '{{count}} positions outside the planned scope will be recorded as unplanned.',
        },
      },
    },
  },
};
